import { Pipe, PipeTransform, ChangeDetectorRef, OnDestroy, EventEmitter, Injectable } from '@angular/core';
import { TranslateService, TranslationChangeEvent } from '@ngx-translate/core';
import { Observable ,  forkJoin ,  Subject ,  Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';

class TranslationInfo {
  constructor(public value: any, public args: any[]) { }
}

/**
 * @whatItDoes Provides a base class that makes it easy to implement Observable-based translation pipes.
 *
 * @description
 * To use this base class, override the `performTranslation` function. This function will receive a value
 * (the subject of the pipe) and any pipe parameters in an `args` array, and should return an `Observable<string>`
 * (this is the default return value of `TranslateService.get()`). Subclasses can access an instance of
 * `TranslateService` via the `translateService` property.
 *
 * Subclasses can also override the `compareArgs` function; this function is used to determine when a call
 * is a duplicate and can be safely ignored. Note that this function must return true when arguments match
 * the previous call; if it always returns false, the pipe will attempt to perform translation continually.
 */
@Injectable()
export class TranslationBasePipe implements PipeTransform, OnDestroy {
  private _resultValue: any;
  private _resultArgs: any[];
  private _result = '';

  private _translationChangeHandler: Subscription;
  private _translationSubscription: Subscription;
  private _translationSubject: Subject<TranslationInfo>;

  protected get translateService(): TranslateService {
    return this._translateService;
  }

  constructor(private _translateService: TranslateService, private _changeDetector: ChangeDetectorRef) { }

  ngOnDestroy(): void {
    this._unsubscribeEvents();
    this._unsubscribeTranslation();
  }

  transform(value: any, ...args: any[]): any {
    return this._transform(value, args, false);
  }

  private _transform(value: any, args: any[], force: boolean): string {
    if (!force && this.compareArgs(this._resultValue, this._resultArgs, value, args)) {
      return this._result;
    }

    this._resultValue = value;
    this._resultArgs = args;
    this._result = '';

    this._subscribeEvents();

    this._beginTranslation();

    return this._result;
  }

  protected performTranslation(value: any, args: any[]): Observable<string> {
    throw new Error('performTranslation must be reimplemented.');
  }

  protected compareArgs(oldValue: any, oldArgs: any[], newValue: any, newArgs: any[]) {
    if (oldValue !== newValue) {
      return false;
    }

    if (!oldArgs !== !newArgs) {
      return false;
    }
    if (!oldArgs) {
      return true;
    }

    if (oldArgs.length !== newArgs.length) {
      return false;
    }

    for (let i = 0; i < oldArgs.length; i++) {
      if (oldArgs[i] !== newArgs[i]) {
        return false;
      }
    }

    return true;
  }

  private _beginTranslation() {
    if (!this._translationSubject) {
      this._translationSubject = new Subject<TranslationInfo>();

      const translationObserver = this._translationSubject.pipe(
        switchMap((info: TranslationInfo) => this.performTranslation(info.value, info.args))
      );

      this._translationSubscription = translationObserver.subscribe(translated => {
        this._result = translated;
        this._changeDetector.markForCheck();
      });
    }

    this._translationSubject.next(new TranslationInfo(this._resultValue, this._resultArgs));
  }

  private _unsubscribeTranslation() {
    if (this._translationSubscription) {
      this._translationSubscription.unsubscribe();
      this._translationSubscription = null;
    }
    if (this._translationSubject) {
      this._translationSubject.complete();
      this._translationSubject = null;
    }
  }

  private _subscribeEvents() {
    if (!this._translationChangeHandler) {
      this._translationChangeHandler = this._translateService.onTranslationChange.subscribe((event: TranslationChangeEvent) => {
        if (this._resultArgs) {
          this._transform(this._resultValue, this._resultArgs, true);
        }
      });
    }
  }

  private _unsubscribeEvents() {
    if (this._translationChangeHandler) {
      this._translationChangeHandler.unsubscribe();
      this._translationChangeHandler = null;
    }
  }
}
